import { BaseAdapter } from "./base-adapter";
import {
  TEvent,
  EventType,
  IPageViewEvent,
  IAddToCartEvent,
  ICheckoutStartedEvent,
  ICheckoutCompletedEvent,
  IProductViewEvent,
  ISearchEvent,
  IAddPaymentInfoEvent,
} from "../types";

declare global {
  interface Window {
    fbq?: (
      type: string,
      eventName: string,
      params?: Record<string, any>
    ) => void;
  }
}

/**
 * Configuration for the Facebook Pixel adapter
 */
export interface PixelAdapterConfig {
  /**
   * Facebook Pixel ID
   */
  pixelId: string;
}

/**
 * Facebook Pixel tracking adapter
 * Assumes that the Facebook Pixel script is already loaded in the page
 */
export class PixelAdapter extends BaseAdapter {
  public readonly name = "facebook-pixel";

  /**
   * Constructor
   * @param config Configuration for the adapter
   */
  constructor(config: PixelAdapterConfig) {
    super(config);
  }

  /**
   * Initialize the Facebook Pixel
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      console.log("FB Pixel: Already initialized");
      return;
    }

    // Skip initialization if running on server
    if (typeof window === "undefined") {
      console.log("FB Pixel: Skipping initialization on server");
      return;
    }

    // Check if fbq is available
    if (!window.fbq) {
      console.warn(
        "FB Pixel: Facebook Pixel fbq not found. Make sure the script is loaded."
      );
      return;
    }

    console.log("FB Pixel: Successfully initialized");
    this.initialized = true;
  }

  /**
   * Track an event with Facebook Pixel
   * @param event The event to track
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (!this.initialized || typeof window === "undefined" || !window.fbq) {
      console.warn("FB Pixel: Cannot track event, adapter not initialized or fbq not available", event.type);
      return;
    }

    console.log(`FB Pixel: Tracking event ${event.type}`);

    switch (event.type) {
      case EventType.PAGE_VIEW:
        this.trackPageView(event);
        break;
      case EventType.PRODUCT_VIEW:
        this.trackProductView(event);
        break;
      case EventType.ADD_TO_CART:
        this.trackAddToCart(event);
        break;
      case EventType.CHECKOUT_STARTED:
        this.trackCheckoutStarted(event);
        break;
      case EventType.CHECKOUT_COMPLETED:
        this.trackCheckoutCompleted(event);
        break;
      case EventType.ADD_PAYMENT_INFO:
        this.trackAddPaymentInfo(event);
        break;
      case EventType.SEARCH:
        this.trackSearch(event);
        break;
      default:
        console.warn(`FB Pixel: Unhandled event type "${event.type}"`);
        // For other events, track as custom events
        // const params = this.eventToParams(event);
        // window.fbq("trackCustom", event.type, this.enhanceWithAffiliateParams(params));
        // break;
    }
  }

  /**
   * Track a page view event
   * @param event The page view event
   */
  private trackPageView(event: IPageViewEvent): void {
    window.fbq!("track", "PageView", this.enhanceWithAffiliateParams({}));
  }

  /**
   * Track a product view event
   * @param event The product view event
   */
  private trackProductView(event: IProductViewEvent): void {
    const params = {
      content_type: "product_group",
      content_ids: [event.productId],
      content_name: event.productName,
      content_category: event.category,
      value: event.price,
      currency: event.currency || "INR",
    };
    
    window.fbq!("track", "ViewContent", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track an add to cart event
   * @param event The add to cart event
   */
  private trackAddToCart(event: IAddToCartEvent): void {
    const params = {
      content_type: "product_group",
      content_ids: [event.productId],
      content_name: event.productName,
      value: event.price,
      currency: event.currency || "INR",
    };
    
    window.fbq!("track", "AddToCart", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track a checkout started event
   * @param event The checkout started event
   */
  private trackCheckoutStarted(event: ICheckoutStartedEvent): void {
    const params = {
      content_ids: event.items.map((item) => item.productId),
      content_type: 'product_group',
      value: event.cartValue,
      currency: event.currency || "INR",
      num_items: event.itemCount,
      contents: event.items.map((item) => ({
        id: item.productId,
        quantity: item.quantity,
        item_price: item.price,
      })),
    };
    
    window.fbq!("track", "InitiateCheckout", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track a checkout completed event
   * @param event The checkout completed event
   */
  private trackCheckoutCompleted(event: ICheckoutCompletedEvent): void {
    const params = {
      value: event.cartValue,
      currency: event.currency || "INR",
      content_type: "product_group",
      content_ids: event.items.map((item) => item.productId),
      num_items: event.itemCount,
      contents: event.items.map((item) => ({
        id: item.productId,
        quantity: item.quantity,
        item_price: item.price,
      })),
    };
    
    window.fbq!("track", "Purchase", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track a search event
   * @param event The search event
   */
  private trackSearch(event: ISearchEvent): void {
    const params = {
      search_string: event.searchTerm,
      content_category: "product",
      content_ids: event.content_ids || [],
    };
    
    window.fbq!("track", "Search", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Track an add payment info event
   * @param event The add payment info event
   */
  private trackAddPaymentInfo(event: IAddPaymentInfoEvent): void {
    const params = {
      value: event.cartValue,
      currency: event.currency || "INR",
      content_type: "product_group",
      content_ids: event.items.map((item) => item.productId),
      contents: event.items.map((item) => ({
        id: item.productId,
        quantity: item.quantity,
        item_price: item.price,
      })),
      payment_type: event.paymentType || "",
    };
    
    window.fbq!("track", "AddPaymentInfo", this.enhanceWithAffiliateParams(params));
  }

  /**
   * Convert an event to Facebook Pixel parameters
   * @param event The event to convert
   */
  private eventToParams(event: TEvent): Record<string, any> {
    // Remove type and timestamp from the event
    const { type, timestamp, ...params } = event;
    return this.enhanceWithAffiliateParams(params);
  }
}
